import api from './connectionProxy'
import { isProcessing } from '../constants/vectorization'

const normalizeBooksResponse = function (response, fallback) {
  const data = response.data || {}
  const pagination = data.pagination || {}
  const books = data.livros || data.items || []
  const fallbackPage = (fallback || {}).page || 0
  const fallbackPerPage = (fallback || {}).perPage || 50

  return {
    items: books,
    length: pagination.total || data.total || books.length,
    page: typeof pagination.page === 'number' ? pagination.page - 1 : fallbackPage,
    rowsPerPage: pagination.limit || data.limit || fallbackPerPage,
  }
}

const mapBookItem = function (livro) {
  livro = livro || {}

  const createdAt = livro.created_at || livro.data || null
  const status = livro.status || 'pendente'
  const arquivo = livro.arquivo || {}
  const processamento = livro.processamento || {}
  const tamanhoBytes = arquivo.tamanho_bytes || livro.tamanho_bytes || 0

  return {
    id: livro.id || livro.isbn,
    book_name: livro.titulo || livro.book_name || '',
    isbn: livro.isbn || '',
    created_at: createdAt,
    vectorized_at: livro.vectorized_at || (status === 'vetorizado' ? createdAt : null),
    autores: livro.autores || [],
    ano: livro.ano || null,
    especialidade: livro.especialidade || null,
    tamanho_bytes: tamanhoBytes,
    processamento: {
      id: processamento.id || livro.id || livro.isbn || null,
      status_atual: processamento.status_atual || status,
      criado_em: processamento.criado_em || createdAt,
      atualizado_em: processamento.atualizado_em || createdAt,
      arquivo_nome: processamento.arquivo_nome || arquivo.nome || null,
      ciclo_uuid: processamento.ciclo_uuid || livro.ciclo_uuid || null,
    },
    arquivo: {
      id: arquivo.id || null,
      tamanho_bytes: tamanhoBytes,
      criado_em: arquivo.criado_em || createdAt,
      etapa: arquivo.etapa || null,
      s3_bucket: arquivo.s3_bucket || null,
      s3_chave: arquivo.s3_chave || null,
      content_type: arquivo.content_type || null,
    },
    status: {
      id: null,
      status: status,
      ativo: true,
      iniciado_em: createdAt,
      finalizado_em: status === 'vetorizado' ? createdAt : null,
    },
    raw: livro,
  }
}

const normalizeText = function (value) {
  return String(value || '').trim()
}

const normalizeTextList = function (items) {
  if (!items) return []

  if (!Array.isArray(items)) {
    items = String(items)
      .split(/[,;]+/)
      .map(normalizeText)
  }

  return items
    .map(function (item) {
      if (item && typeof item === 'object') {
        return normalizeText(item.label || item.name || item.nome || item.value || item._id || item.id)
      }

      return normalizeText(item)
    })
    .filter(Boolean)
}

const getUploadContentType = function (arquivo) {
  const filename = normalizeText((arquivo || {}).name).toLowerCase()

  if (filename.endsWith('.epub')) return 'application/epub+zip'
  if (filename.endsWith('.pdf')) return 'application/pdf'

  return (arquivo && arquivo.type) || 'application/octet-stream'
}

export const getBookVectorizations = function (params) {
  params = params || {}
  const page = params.page || 0
  const perPage = params.perPage || 50
  const order = params.order || {}
  const term = params.term || ''

  const columnMap = {
    book_name: 'titulo',
    isbn: 'isbn',
    autores: 'autores',
    especialidade: 'especialidade',
    ano: 'ano',
    created_at: 'created_at',
    status: 'status',
    tamanho_bytes: 'tamanho_bytes',
  }

  const apiParams = {
    page: page + 1,
    limit: perPage,
    order_by: columnMap[order.column] || 'created_at',
    order: order.direction === -1 ? 'asc' : 'desc',
  }

  if (term) {
    apiParams.search = term
  }

  return api.vectorization.get('/api/v1/livros', { params: apiParams }).then(function (response) {
    const normalized = normalizeBooksResponse(response, { page, perPage })

    return {
      data: {
        items: normalized.items.map(mapBookItem),
        length: normalized.length,
        page: normalized.page,
        rowsPerPage: normalized.rowsPerPage,
        order: order,
      },
    }
  })
}

export const getBookVectorization = function (id) {
  return api.vectorization
    .get('/api/v1/livros', { params: { page: 1, limit: 100, search: id } })
    .then(function (response) {
      const items = normalizeBooksResponse(response, { page: 0, perPage: 100 }).items
      const livro = items.find(function (item) {
        return item.id === id || item.isbn === id
      })

      if (!livro) {
        return Promise.reject({
          response: {
            data: { message: 'Livro não encontrado' },
            status: 404,
          },
        })
      }

      return { data: mapBookItem(livro) }
    })
}

export const addBookVectorization = function (bookData) {
  var arquivo = bookData.arquivo
  var titulo = normalizeText(bookData.titulo || bookData.book_name)
  var isbn = normalizeText(bookData.isbn)
  var contentType = getUploadContentType(arquivo)

  // Callback de progresso (se fornecido)
  var onProgress = bookData.onProgress || function () {}

  if (!titulo || !isbn || !arquivo || !arquivo.name) {
    return Promise.reject({
      response: {
        data: { message: 'Título, ISBN e arquivo são obrigatórios para vetorizar livro.' },
        status: 400,
      },
    })
  }

  // Passo 1: POST JSON com metadados — API retorna presigned URL para upload S3
  var metadata = {
    isbn: isbn,
    titulo: titulo,
    autores: normalizeTextList(bookData.autores),
    filename: arquivo.name,
    content_type: contentType,
  }

  if (bookData.ano) {
    var ano = parseInt(bookData.ano, 10)
    if (!Number.isNaN(ano)) {
      metadata.ano = ano
    }
  }
  if (bookData.especialidade) {
    metadata.especialidade = normalizeText(bookData.especialidade)
  }

  onProgress({ step: 'register', message: 'Registrando livro na API...' })

  return api.vectorization
    .post('/api/v1/livros', metadata)
    .then(function (response) {
      var resData = response.data || {}
      var upload = resData.upload || {}

      console.log('[BookUpload] Passo 1 OK — isbn:', isbn, 'processamento_id:', resData.processamento_id, 'upload.url:', upload.url ? 'presente' : 'AUSENTE')

      // Passo 2: PUT binário direto no S3 via presigned URL
      // Usar fetch (não axios) para evitar headers extras que causam CORS preflight
      if (arquivo && !upload.url) {
        console.error('[BookUpload] ERRO: API não retornou presigned URL. Response:', JSON.stringify(resData))
        return Promise.reject({
          response: {
            data: { message: 'URL de upload não foi retornada pela API.' },
            status: 500,
          },
        })
      }

      if (arquivo && upload.url) {
        var uploadHeaders = upload.headers || {}
        var uploadContentType = uploadHeaders['Content-Type'] || uploadHeaders['content-type'] || contentType

        onProgress({ step: 'uploading', message: 'Enviando arquivo para o servidor...' })
        console.log('[BookUpload] Passo 2 — Iniciando PUT ao S3. Tamanho:', arquivo.size, 'bytes, Content-Type:', uploadContentType)

        return fetch(upload.url, {
          method: 'PUT',
          headers: { 'Content-Type': uploadContentType },
          body: arquivo,
        }).then(function (uploadResponse) {
          if (!uploadResponse.ok) {
            console.error('[BookUpload] ERRO no PUT S3. Status:', uploadResponse.status, 'StatusText:', uploadResponse.statusText)
            return Promise.reject({
              handledUploadError: true,
              response: {
                data: {
                  message:
                    'Erro no upload do arquivo para o servidor. Status: ' +
                    uploadResponse.status,
                },
                status: uploadResponse.status,
              },
            })
          }

          var etag = uploadResponse.headers.get('ETag') || ''
          console.log('[BookUpload] Passo 2 OK — PUT S3 concluído. Status:', uploadResponse.status, 'ETag:', etag)

          // Pipeline é disparado automaticamente via S3 ObjectCreated event
          onProgress({ step: 'uploaded', message: 'Arquivo enviado. Pipeline iniciado automaticamente.' })

          return {
            data: mapBookItem({
              id: resData.processamento_id || resData.id || isbn,
              titulo: titulo,
              isbn: isbn,
              autores: bookData.autores || [],
              ano: bookData.ano || null,
              especialidade: bookData.especialidade || null,
              status: resData.status || 'UPLOAD_INICIADO',
              created_at: new Date().toISOString(),
              ciclo_uuid: resData.ciclo_uuid || null,
              arquivo: {
                tamanho_bytes: arquivo.size,
                content_type: contentType,
                nome: arquivo.name,
                s3_chave: upload.s3_key || null,
              },
            }),
            isbn: isbn,
            uploadConfirmed: true,
          }
        }).catch(function (fetchError) {
          if (fetchError && fetchError.handledUploadError) {
            return Promise.reject(fetchError)
          }

          // fetch rejeita com TypeError em falha de rede/CORS
          console.error('[BookUpload] ERRO de rede/CORS no PUT S3:', fetchError.message || fetchError)
          return Promise.reject({
            response: {
              data: {
                message: 'Falha de rede ao enviar arquivo. Verifique CORS e conectividade. Detalhe: ' + (fetchError.message || 'Erro desconhecido'),
              },
              status: 0,
            },
          })
        })
      }

      // Sem arquivo — retorna apenas o registro criado
      console.warn('[BookUpload] Nenhum arquivo fornecido — livro registrado sem upload.')
      return {
        data: mapBookItem({
          id: resData.processamento_id || resData.id || isbn,
          titulo: titulo,
          isbn: isbn,
          autores: bookData.autores || [],
          ano: bookData.ano || null,
          especialidade: bookData.especialidade || null,
          status: resData.status || 'pendente',
          created_at: new Date().toISOString(),
          ciclo_uuid: resData.ciclo_uuid || null,
        }),
        isbn: isbn,
        uploadConfirmed: false,
      }
    })
}

export const getBookStatus = function (isbn) {
  return api.vectorization.get('/api/v1/livros/' + isbn + '/status')
}

export const reprocessBook = function (isbn) {
  return api.vectorization.post('/api/v1/livros/' + isbn + '/reprocessar')
}

export const addStructuredBookVectorization = function (formData) {
  return api.vectorization.post('/api/v1/livros/estruturados', formData, { timeout: 0 })
}

export const deleteBookVectorization = function () {
  return Promise.reject({
    response: { data: { message: 'A collection atual não expõe exclusão de livros.' }, status: 501 },
  })
}

export const editBookVectorization = function () {
  return Promise.reject({
    response: { data: { message: 'A collection atual não expõe edição de livros.' }, status: 501 },
  })
}

export const restoreBookVectorization = function () {
  return Promise.reject({
    response: { data: { message: 'A collection atual não expõe restauração de livros.' }, status: 501 },
  })
}

export const executeProcessing = function () {
  return Promise.reject({
    response: {
      data: { message: 'A collection atual não expõe /api/v1/processamentos/executar.' },
      status: 501,
    },
  })
}

export const getBookFiles = function (isbn) {
  return api.vectorization.get('/api/v1/livros/' + isbn + '/arquivos')
}

export const getBookChapters = function (isbn) {
  return api.vectorization.get('/api/v1/livros/' + isbn + '/capitulos')
}

export const searchBooks = function (query, isbn, topK, filters) {
  filters = filters || {}

  return api.vectorization.post('/api/v1/buscar', {
    query: query,
    isbn: isbn || null,
    top_k: topK || 15,
    score_minimo: filters.scoreMinimo || filters.score_minimo || undefined,
    autores: filters.autores || undefined,
    ano_min: filters.anoMin || filters.ano_min || undefined,
    ano_max: filters.anoMax || filters.ano_max || undefined,
    especialidade: filters.especialidade || undefined,
    tipo: filters.tipo || undefined,
    origem_vetorizacao: filters.origemVetorizacao || filters.origem_vetorizacao || undefined,
    formato_origem: filters.formatoOrigem || filters.formato_origem || undefined,
    vetorUnico: true,
  })
}

export const searchBookVectorsByIsbn = function ({ isbn, query, topK, scoreMinimo }) {
  return searchBooks(query, isbn, topK || 15, {
    scoreMinimo: scoreMinimo,
  })
}

export const deleteVectorsByIdVetor = function (idVetor) {
  return api.vectorization.delete('/api/v1/livros/capitulos/' + idVetor)
}

export const updateIndexarInformed = function (idVetor, indexarInformed) {
  return api.vectorization.patch('/api/v1/livros/capitulos/' + idVetor, { indexarInformed })
}

export const getVectorsHistory = function (params) {
  return api.vectorization.get('/api/v1/livros/capitulos/historico', { params: params || {} })
}

export const countProcessingBooks = function () {
  return api.vectorization
    .get('/api/v1/livros', { params: { page: 1, limit: 100 } })
    .then(function (response) {
      const items = normalizeBooksResponse(response, { page: 0, perPage: 100 }).items
      const processing = items.filter(function (item) {
        return isProcessing(item.status || '')
      })

      return {
        data: {
          total: processing.length,
          limit: 1,
          canUpload: processing.length < 1,
        },
      }
    })
}

export const normalizeBooksListPayload = normalizeBooksResponse



// WEBPACK FOOTER //
// ./src/api/bookVectorization.js